import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, IconButton, InputAdornment, Paper, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { withLocalize, LocalizeContextProps } from 'react-localize-redux';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import { ReduxStore } from '../../../constants/types';
import { Billable, BillItem } from './types';
import { pushBillables } from '../../../redux/actions/billingActions';
import { filterBillablesByCategory, getBillableCategories, getElementBillables, sortBillablesByConcept, PHARMACY_CATEGORY } from './billItemAddHelpers';

// SHER-460 — manual-billing prestation picker. Replaces the old full-width category
// chip-wall that read like page content. Flow: filter first (category chips), then
// search inside it via the "Concept / Prestation" field; clicking a result adds its
// row immediately via pushBillables([{ id }]) — the same path combos use — and the
// panel stays open for the next pick (one-by-one, no batch). Only ELEMENT billables
// are listed here; forfaits/physio/combos are added through their own selectors.

const T = 'hospital.billing.bill.selectors.';

interface Props {
    disabled?: boolean;
    // SHER-460 #4: optional Type selector rendered inline on the search row, so the Type
    // dropdown and the Prestation/Concept field share one line (BillItemAddActionBar passes it).
    typeSelector?: React.ReactNode;
}

// Categorical colour palette for the chips — one stable colour per category, keyed by
// its index in the sorted category list. Mirrors the existing `categoryPillColor`
// approach in BillItems; categories are domain data with no theme token, so a fixed
// palette (not theme.palette) is the right tool here.
const CATEGORY_PALETTE = [
    '#BA7517', '#378ADD', '#A32D2D', '#639922', '#534AB7', '#1D9E75',
    '#D85A30', '#D4537E', '#0F6E56', '#185FA5', '#854F0B', '#27500A',
    '#993C1D', '#3C3489', '#72243E', '#0C447C', '#5F5E5A', '#444441', '#085041',
];

const formatAmount = (amount: number) => `${Number(amount ?? 0).toLocaleString('fr-FR')}`;

const PrestationSelectorInner: React.FC<Props & LocalizeContextProps> = ({ disabled, typeSelector, translate }) => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const billables = useSelector((state: ReduxStore) => state.billing.data.billablesCurrentBill ?? []);
    const currentBillItems = useSelector((state: ReduxStore) => state.billing.data.billItems ?? []);
    const loadingBillables = useSelector((state: ReduxStore) => state.billing.loading);
    const [category, setCategory] = useState('');
    const [query, setQuery] = useState('');
    // SHER-460 #8: once staff finish picking, they can collapse the catalogue so it stops
    // sitting in the middle of the screen while they review the bill; a button re-opens it.
    const [collapsed, setCollapsed] = useState(false);

    // SHER-460 #7: mark catalogue items already on the bill (by billableId) so staff can see
    // what they've added without losing their place; clicking still adds another (Pedro's ask).
    const addedBillableIds = useMemo(
        () => new Set((currentBillItems as BillItem[]).map((item) => item.billableId).filter((id) => id != null)),
        [currentBillItems],
    );

    const elementBillables = useMemo(() => getElementBillables(billables as Billable[]), [billables]);
    const categories = useMemo(() => getBillableCategories(elementBillables), [elementBillables]);
    const colorMap = useMemo(() => {
        const map = new Map<string, string>();
        categories.forEach((c, i) => map.set(c, CATEGORY_PALETTE[i % CATEGORY_PALETTE.length]));
        return map;
    }, [categories]);

    const results = useMemo(() => {
        const byCategory = filterBillablesByCategory(elementBillables, category || null);
        const q = query.trim().toLowerCase();
        const filtered = q ? byCategory.filter((b) => (b.concept ?? '').toLowerCase().includes(q)) : byCategory;
        // SHER-460 #3: show the catalogue alphabetically by prestation, both for the full list and
        // within a filtered category/search, so staff can scan long lab/radiology lists quickly.
        return sortBillablesByConcept(filtered);
    }, [elementBillables, category, query]);

    const colorFor = (c: string) => colorMap.get(c) ?? theme.palette.grey[600];

    const onAdd = (b: Billable) => {
        if (disabled) return;
        dispatch(pushBillables([{ id: b.id }]));
    };

    // Match BillItemAddActionBar: don't render against a stale/empty list while the
    // billables reload (e.g. after an insurance change).
    if (loadingBillables) return null;

    const t = (key: string) => translate(`${T}${key}`).toString();
    const allLabel = t('all_categories');
    // SHER-460 #10: the pharmacy sentinel is a filter value, never shown raw — render the label.
    const categoryLabel = (value: string) => (value === PHARMACY_CATEGORY ? t('type_pharmacy') : value);

    const chip = (label: string, value: string) => {
        const active = category === value;
        const c = value ? colorFor(value) : theme.palette.grey[600];
        return (
            <Box
                key={label}
                component="button"
                type="button"
                disabled={disabled}
                onClick={() => setCategory(value)}
                sx={{
                    // SHER-460 #2: larger pill-shaped chips with more padding and a clearer selected
                    // state (filled + bold + ring), so billing staff can read and click categories
                    // fast even when many are shown.
                    flex: '0 0 auto', cursor: disabled ? 'default' : 'pointer', fontSize: 13, lineHeight: 1.4,
                    px: 2, py: 0.75, borderRadius: 999, fontWeight: active ? 600 : 500, whiteSpace: 'nowrap',
                    border: `1px solid ${active ? c : `${c}55`}`,
                    backgroundColor: active ? c : `${c}18`, color: active ? '#fff' : c,
                    boxShadow: active ? `0 0 0 2px ${c}44` : 'none',
                    transition: 'background-color 120ms, box-shadow 120ms',
                }}
            >
                {label}
            </Box>
        );
    };

    return (
        <Box data-testid="prestation-selector" sx={{ width: '100%', mb: 2 }}>
            {/* SHER-460 #4: Type selector (when present) and the search field share one line. */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                {typeSelector ? <Box sx={{ flex: '0 0 auto', minWidth: 200, maxWidth: 240 }}>{typeSelector}</Box> : null}
                <TextField
                    size="small"
                    fullWidth
                    sx={{ flex: 1 }}
                    label={t('select_prestation')}
                    placeholder={t('search_placeholder')}
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); if (collapsed) setCollapsed(false); }}
                    disabled={disabled}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                                {query ? (
                                    <IconButton size="small" edge="start" aria-label={t('clear')} data-testid="prestation-clear" onClick={() => setQuery('')} sx={{ ml: 0.25 }}>
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                ) : null}
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>
            {/* SHER-460 #8: collapsed → a single re-open button instead of the full panel. */}
            {collapsed ? (
                <Button data-testid="prestation-reopen" size="small" variant="outlined" onClick={() => setCollapsed(false)} disabled={disabled} sx={{ mt: 0.75, textTransform: 'none' }}>
                    {t('show_catalogue')}
                </Button>
            ) : (
            <Paper elevation={0} sx={{ mt: 0.75, borderRadius: 2, overflow: 'hidden', border: '1px solid #d4d4d4', boxShadow: '0 6px 18px -8px rgba(0,0,0,0.25)' }}>
                {/* tinted header with an accent bar so the panel reads as its own component */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1.75, py: 1.25, backgroundColor: '#f4f8fd', borderBottom: '1px solid #e3edf7' }}>
                    <Box sx={{ width: 4, height: 18, borderRadius: 1, backgroundColor: theme.palette.primary.main }} />
                    <Typography variant="subtitle2" fontWeight={500} sx={{ flex: 1 }}>{t('select_prestation_title')}</Typography>
                    <Box component="span" sx={{ fontSize: 11, px: 1.1, py: 0.25, borderRadius: 11, color: category ? colorFor(category) : 'text.secondary', backgroundColor: category ? `${colorFor(category)}22` : '#eee' }}>
                        {category ? categoryLabel(category) : allLabel} · {results.length}
                    </Box>
                    {/* SHER-460 #8: clear control to hide the catalogue once picking is done. */}
                    <IconButton size="small" edge="end" aria-label={t('close_catalogue')} data-testid="prestation-close" onClick={() => setCollapsed(true)} disabled={disabled}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
                {/* category chips — wrap into a few rows, capped with internal scroll */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, px: 1.75, py: 1.25, maxHeight: 96, overflowY: 'auto', borderBottom: '1px solid #eee', backgroundColor: '#fafafa' }}>
                    {chip(allLabel, '')}
                    {categories.map((c) => chip(categoryLabel(c), c))}
                </Box>
                {/* results in a responsive grid → fills the width, collapses to 1 column on small laptops */}
                <Box data-testid="prestation-results" sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1px', backgroundColor: '#eee', maxHeight: 240, overflowY: 'auto' }}>
                    {results.map((b) => {
                        // SHER-460 #7: an item already on the bill is greyed with a check; still clickable.
                        const added = addedBillableIds.has(b.id);
                        return (
                        <Box key={b.id} onClick={() => onAdd(b)} data-testid={added ? 'prestation-result-added' : 'prestation-result'}
                            sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, backgroundColor: added ? '#f2f5f2' : '#fff', cursor: disabled ? 'default' : 'pointer', '&:hover': disabled ? {} : { backgroundColor: '#f7f7f7' } }}>
                            {added
                                ? <CheckCircleIcon fontSize="small" sx={{ color: theme.palette.success.main }} />
                                : <AddIcon fontSize="small" sx={{ color: theme.palette.success.main }} />}
                            <Typography variant="body2" sx={{ flex: 1, color: added ? 'text.secondary' : 'text.primary' }}>{b.concept}</Typography>
                            <Typography variant="caption" color="textSecondary">{formatAmount(b.amount)}</Typography>
                        </Box>
                        );
                    })}
                </Box>
                <Box sx={{ px: 1.75, py: 0.75, borderTop: '1px solid #eee', backgroundColor: '#fafafa' }}>
                    <Typography variant="caption" color="textSecondary">{t('add_hint')}</Typography>
                </Box>
            </Paper>
            )}
        </Box>
    );
};

export const PrestationSelector = withLocalize(PrestationSelectorInner);

export default PrestationSelector;
